import { graphql } from 'gatsby';
import React from 'react';
import slugify from 'slugify';
import Seo from '../../components/SEO';
import Cover, {
  CoverHeading,
  CoverLead,
  CoverPrice,
} from '../../components/Cover/Cover';
import FeaturedTestimonials from '../../components/FeaturedTestimonials/FeaturedTestimonials';
import HeaderTheme from '../../components/Header/HeaderTheme';
import LocationCard from '../../components/LocationCard/LocationCard';
import Map, { Marker } from '../../components/Map/Map';
import PageBuilder, { getSpacerSize, getHasSpacer } from '../../components/PageBuilder/PageBuilder';
import Promotions from '../../components/Promotions/Promotions';
import RecentPosts from '../../components/RecentPosts/RecentPosts';
import SplitBody from '../../components/SplitBody/SplitBody';
import Button, { themes, sizes } from '../../components/UI/Button/Button';
import Content from '../../components/UI/Content/Content';
import Navbar from '../../components/UI/Navbar/Navbar';
import Teaser from '../../components/UI/Teaser/Teaser';
import usePromotions from '../../hooks/usePromotions';

const LocationMap = ({ location, details }) => {
  return (
    <Map zoom={details.mapLocation.zoom}>
      <Marker
        coords={[
          details.mapLocation.longitude,
          details.mapLocation.latitude,
        ]}
        popup={
          <LocationCard
            isCard
            title={location.title}
            path={location.uri}
            address={details.address}
            telephone={details.telephone}
            image={details.gallery[ 0 ]}
          />
        }
        isPopupOpen={true}
      />
    </Map>
  );
}

const ServiceLocalTemplate = ({
  data: {
    page: {  
      blocks,
      template: { 
        cover,
        navbar,
        serviceLocal: {
          showServiceLink,
          relatedService,
          relatedLocation: {
            locationDetails,
            ...location
          }
        },
        sectionContent,
        sectionPromotions
      }, 
      metadata,
      seo
    }
  },
  location: { pathname }
}) => {
  let navigation = [
    { label: 'Overview', path: '#overview' }
  ];

  if( blocks.pagebuilder ) {
    navigation = [
      ...navigation,
      ...blocks.pagebuilder
        .filter(({ ...item }) => item.showInNavbar)
        .map(({ ...item }) => ({
          label: item.navbarLabel,
          path: `#${slugify(item.navbarLabel, { lower: true })}`,
        }))
      ]
  }
  
  const firstBlock = blocks.pagebuilder ? blocks.pagebuilder[0] : null;
  const splitBodySpacerSize = getSpacerSize(firstBlock);
  const splitBodyHasSpacer = getHasSpacer(firstBlock);
  const promotions = usePromotions(sectionPromotions.promotions, [ relatedService ], {
    excludePath: pathname
  });

  return (
    <>
      <HeaderTheme isReverse/>

      <Seo 
        description={metadata.metaDescription || cover.lead} 
        image={ metadata.metaImage }
        seo={seo}
      />

      <Cover image={cover.image} hasSpacer>
        <Teaser>
          {relatedService?.servicesCategory?.nodes[ 0 ]?.name}
        </Teaser>
        {cover.heading && <CoverHeading>{cover.heading}</CoverHeading>}
        {cover.lead && <CoverLead>{cover.lead}</CoverLead>}
        {cover.price && <CoverPrice>{cover.price}</CoverPrice>}
      </Cover>

      {!!navigation?.length && (
        <Navbar
          nodes={navigation}
          customButton={navbar.customButton}
          showReferral={navbar.buttons?.includes('refer')}
          showContact={navbar.buttons?.includes('contact')}
          showBook={navbar.buttons?.includes('book')}
          showCustom={navbar.buttons?.includes('custom')}
        />
      )}

      <SplitBody 
        id="overview" 
        isAsideOffset 
        hasSpacer={splitBodyHasSpacer} 
        spacerSize={splitBodySpacerSize} 
        aside={<LocationMap location={location} details={locationDetails}/>}
        >
        <Content items={sectionContent.body} />

        {showServiceLink &&
          <Button
            to={relatedService.uri}
            theme={themes.LINK}
            size={sizes.LARGE}
          >
            Learn more about our {relatedService.title} service
          </Button>
        }
        
      </SplitBody>

      <PageBuilder blocks={blocks.pagebuilder} />

      <FeaturedTestimonials showAsCarousel />
      <Promotions items={promotions} />
      <RecentPosts />
    </>
  );
};

export const query = graphql`
  query ServiceLocalTemplate($id: String!) {
    page: wpPage(id: { eq: $id }) {
      ...Metadata
      blocks {
        ...PageBuilder
      }
      template {
        ...Cover
        ... on WpTemplate_Servicelocal {
          navbar {
            buttons
            customButton {
              label
              link
            }
          }
          sectionContent {
            body {
              ... on  WpTemplate_Servicelocal_Sectioncontent_Body_LayoutWysiwyg {
                fieldGroupName
                wysiwyg
              }
              ... on  WpTemplate_Servicelocal_Sectioncontent_Body_LayoutImage {
                fieldGroupName
                image {
                  title
                  ...WpImage
                }
              }
              ... on  WpTemplate_Servicelocal_Sectioncontent_Body_LayoutVideo {
                fieldGroupName
                caption
                video
              }
            }
          }
          serviceLocal {
            showServiceLink
            relatedService {
              ... on WpService {
                id
                title
                uri
                servicesCategory {
                  nodes {
                    name
                  }
                }
              }
            }
            relatedLocation {
              ... on WpLocation {
                uri: uriRedirect
                title
                locationDetails {
                  address
                  telephone
                  gallery {
                    ...WpImage
                    title
                  }
                  mapLocation {
                    zoom
                    longitude
                    latitude
                  }
                }
              }
            }
          }
          sectionPromotions {
            promotions {
              promotion {
                ...Promotion
              }
            }
          }
        }
      }
    }
  }
`;

export default ServiceLocalTemplate;
